import "./styles.css";

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useScreen } from "Screen/ScreenContext";
import ChargebackDetails from "./ChargebackDetails";

import {
  IconAlertTriangle,
  IconBuildingBank,
  IconCreditCard,
  IconMenu2,
  IconMoneybag,
  IconReceipt,
  IconRepeat,
  IconWallet,
  IconX,
} from "@tabler/icons-react";

import BankOnboardingCard from "./components/BankOnboardingCard";
import WalletConnectionCards from "./components/WalletConnectionCards";
import api from "@helpers/api";
import ChargesHistorySection from "./tabs/Charges";
import SubscriptionsSection from "./tabs/Subscriptions";
import WithdrawalSection from "./tabs/Withdrawals";
import ChargebacksSection from "./tabs/Chargebacks";
import { HelpdeskSupport, Text } from "@fragments";
import {
  useWallets,
  WALLET_PROVIDERS,
  walletIsConnected,
} from "@helpers/wallets";

// Built from the caller's `t` rather than declared at module level, so every
// label follows the active language. `key`, `path` and `match` are routing
// data and stay untranslated.
const getBankingScreens = (t) => [
  {
    key: "wallets",
    label: t("screens.wallets.label"),
    sidebarTitle: t("screens.wallets.sidebarTitle"),
    screenTitle: t("screens.wallets.label"),
    title: t("screens.wallets.title"),
    subtitle: t("screens.wallets.subtitle"),
    screenUrl: "banking/wallet",
    path: "/banking/wallet",
    showInSidebar: true,
    icon: <IconWallet />,
    match: (path) => path === "/banking" || path.includes("/banking/wallet"),
  },
  {
    key: "history",
    label: t("common:entities.charges"),
    sidebarTitle: t("screens.charges.sidebarTitle"),
    screenTitle: t("common:entities.charges"),
    title: t("screens.charges.title"),
    subtitle: t("screens.charges.subtitle"),
    screenUrl: "banking/charges",
    path: "/banking/charges",
    showInSidebar: true,
    icon: <IconReceipt />,
    match: (path) => path.includes("/banking/charges"),
  },
  {
    key: "subscriptions",
    label: t("common:entities.subscriptions"),
    sidebarTitle: t("screens.subscriptions.sidebarTitle"),
    screenTitle: t("common:entities.subscriptions"),
    title: t("screens.subscriptions.title"),
    subtitle: t("screens.subscriptions.subtitle"),
    screenUrl: "banking/subscriptions",
    path: "/banking/subscriptions",
    showInSidebar: true,
    icon: <IconRepeat />,
    match: (path) => path.includes("/banking/subscriptions"),
  },
  {
    key: "withdrawals",
    label: t("screens.withdrawals.label"),
    sidebarTitle: t("screens.withdrawals.sidebarTitle"),
    screenTitle: t("screens.withdrawals.label"),
    title: t("screens.withdrawals.title"),
    subtitle: t("screens.withdrawals.subtitle"),
    screenUrl: "banking/withdrawal",
    path: "/banking/withdrawal",
    showInSidebar: true,
    icon: <IconMoneybag />,
    match: (path) => path.includes("/banking/withdrawal"),
  },
  {
    key: "chargebacks",
    label: t("screens.chargebacks.label"),
    sidebarTitle: t("screens.chargebacks.sidebarTitle"),
    screenTitle: t("screens.chargebacks.label"),
    title: t("screens.chargebacks.title"),
    subtitle: t("screens.chargebacks.subtitle"),
    screenUrl: "banking/chargebacks",
    path: "/banking/chargebacks",
    showInSidebar: true,
    icon: <IconAlertTriangle />,
    match: (path) => path.includes("/banking/chargebacks"),
  },
  {
    key: "anticipations",
    label: t("screens.anticipations.label"),
    screenTitle: t("screens.anticipations.label"),
    title: t("screens.anticipations.title"),
    subtitle: t("screens.anticipations.subtitle"),
    path: "/banking/anticipations",
    showInSidebar: false,
    icon: <IconCreditCard />,
    match: (path) => path.includes("/banking/anticipations"),
  },
  {
    key: "commissions",
    label: t("screens.commissions.label"),
    sidebarTitle: t("screens.commissions.sidebarTitle"),
    disabled: true,
    badge: t("common:status.comingSoon"),
    showInSidebar: true,
    icon: <IconCreditCard />,
  },
];

const getWalletHelpdeskArticles = (t) => [
  {
    title: t("helpdesk.plans.title"),
    subtitle: t("helpdesk.plans.subtitle"),
    url: "https://help.ginastee.com/pt-br/vendas-planos-e-cupons/como-criar-planos-para-vender-sua-comunidade#onde-acessar",
    icon: <IconReceipt />,
  },
  {
    title: t("helpdesk.wallet.title"),
    subtitle: t("helpdesk.wallet.subtitle"),
    url: "https://help.ginastee.com/pt-br/financeiro-wallet-e-saques/como-configurar-sua-wallet-no-ginastee#status-de-analise",
    icon: <IconWallet />,
  },
  {
    title: t("helpdesk.withdrawal.title"),
    subtitle: t("helpdesk.withdrawal.subtitle"),
    url: "https://help.ginastee.com/pt-br/financeiro-wallet-e-saques/como-solicitar-um-saque",
    icon: <IconMoneybag />,
  },
];

function providerFromSearch(searchParams) {
  return searchParams.get("provider") === WALLET_PROVIDERS.STRIPE
    ? WALLET_PROVIDERS.STRIPE
    : WALLET_PROVIDERS.ASAAS;
}

export default function Banking() {
  document.title = "Banking | Ginastee";

  const { t } = useTranslation("banking");
  const { chargebackId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setBreadcrumb, setFullWidth } = useScreen();
  const returnUrl = searchParams.get("return");

  const {
    asaas,
    stripe,
    loading: loadingWallets,
    refresh: refreshWallets,
    hasAnyConnected,
  } = useWallets();

  // `t` changes identity on a language switch, so the labels re-resolve.
  const bankingScreens = useMemo(() => getBankingScreens(t), [t]);

  const [provider, setProvider] = useState(providerFromSearch(searchParams));
  const [showAsaasOnboarding, setShowAsaasOnboarding] = useState(false);
  const selectedWallet = provider === WALLET_PROVIDERS.STRIPE ? stripe : asaas;

  useEffect(() => {
    setFullWidth(true);
    return () => setFullWidth(false);
  }, [setFullWidth]);

  function handleWalletApproved() {
    refreshWallets();
    setShowAsaasOnboarding(false);
    if (returnUrl) navigate(returnUrl);
  }

  function handleProviderChange(nextProvider) {
    setProvider(nextProvider);
    if (nextProvider !== WALLET_PROVIDERS.ASAAS) {
      setShowAsaasOnboarding(false);
    }
    const nextParams = new URLSearchParams(searchParams);
    // asaas is the URL default — only a stripe selection is worth carrying.
    if (nextProvider === WALLET_PROVIDERS.STRIPE) {
      nextParams.set("provider", nextProvider);
    } else {
      nextParams.delete("provider");
    }
    setSearchParams(nextParams, { replace: true });
  }

  function handleOpenAsaasOnboarding() {
    setShowAsaasOnboarding(true);
  }

  useEffect(() => {
    const urlProvider = providerFromSearch(searchParams);
    if (urlProvider !== provider) setProvider(urlProvider);
  }, [location.search]);

  useEffect(() => {
    if (!returnUrl || loadingWallets || !hasAnyConnected) return;
    navigate(returnUrl, { replace: true });
  }, [returnUrl, loadingWallets, hasAnyConnected, navigate]);

  useEffect(() => {
    if (walletIsConnected(asaas)) {
      setShowAsaasOnboarding(false);
    }
  }, [asaas?.connected]);

  const getInitialScreen = () => {
    const foundScreen = bankingScreens.find(
      (screen) => screen.match && screen.match(location.pathname),
    );
    return foundScreen ? foundScreen.key : "wallets";
  };

  const [screen, setScreen] = useState(getInitialScreen());

  useEffect(() => {
    const newScreen = getInitialScreen();
    if (newScreen !== screen) {
      setScreen(newScreen);
    }
  }, [location.pathname, screen]);

  const activeScreenConfig =
    bankingScreens.find((t) => t.key === screen) || bankingScreens[0];

  useEffect(() => {
    if (chargebackId) return;
    const items = [
      {
        label: "Banking",
        href: "/banking",
        icon: <IconBuildingBank size={18} />,
      },
      ...(activeScreenConfig?.label
        ? [{ label: activeScreenConfig.label, href: activeScreenConfig.path }]
        : []),
    ];
    setBreadcrumb(items);
    return () => setBreadcrumb(null);
  }, [activeScreenConfig?.key, chargebackId]);

  const [chargebackOpenCount, setChargebackOpenCount] = useState(0);

  useEffect(() => {
    api
      .get("charge/chargeback/list?page=1&per_page=1")
      .then(({ data }) => {
        setChargebackOpenCount(data?.summary?.in_progress?.charges ?? 0);
      })
      .catch(() => {});
  }, []);

  function renderActiveScreen() {
    if (screen === "wallets") {
      return (
        <WalletScreen
          asaas={asaas}
          stripe={stripe}
          loading={loadingWallets}
          showAsaasOnboarding={showAsaasOnboarding}
          onProviderChange={handleProviderChange}
          onOpenAsaasOnboarding={handleOpenAsaasOnboarding}
          onRefresh={refreshWallets}
          onWalletApproved={handleWalletApproved}
          t={t}
        />
      );
    }

    if (screen === "withdrawals") {
      return (
        <WithdrawalSection
          provider={provider}
          wallet={selectedWallet}
          loadingWallet={loadingWallets}
          reloadWallets={refreshWallets}
          onProviderChange={handleProviderChange}
        />
      );
    }

    if (screen === "subscriptions")
      return <SubscriptionsSection provider={provider} />;
    if (screen === "chargebacks")
      return <ChargebacksSection provider={provider} />;
    return <ChargesHistorySection provider={provider} />;
  }

  if (chargebackId) {
    return <ChargebackDetails chargebackId={chargebackId} />;
  }

  return (
    <div className="banking-page">
      <div className="banking-layout">
        <BankingSidebar
          items={bankingScreens.filter((item) => item.showInSidebar)}
          currentScreen={screen}
          provider={provider}
          searchParams={searchParams}
          chargebackOpenCount={chargebackOpenCount}
          navigate={navigate}
          t={t}
        />

        <main className="banking-content">
          <div className="banking-screen-content pb-9">
            <div className="banking-page-heading">
              <Text.h4 contrast={6} weight={500} left={activeScreenConfig.icon}>
                {activeScreenConfig.label || activeScreenConfig.screenTitle}
              </Text.h4>
              <Text.h1 weight={500} contrast={10}>
                {activeScreenConfig.title}
              </Text.h1>
              <Text.p contrast={5}>{activeScreenConfig.subtitle}</Text.p>
            </div>

            {renderActiveScreen()}
          </div>
        </main>
      </div>
    </div>
  );
}

function WalletScreen({
  asaas,
  stripe,
  loading,
  showAsaasOnboarding,
  onProviderChange,
  onOpenAsaasOnboarding,
  onRefresh,
  onWalletApproved,
  t,
}) {
  return (
    <div className="banking-wallet-screen">
      <WalletConnectionCards
        asaas={asaas}
        stripe={stripe}
        loading={loading}
        onProviderChange={onProviderChange}
        onOpenAsaasOnboarding={onOpenAsaasOnboarding}
        onRefresh={onRefresh}
      />

      {showAsaasOnboarding && !walletIsConnected(asaas) && (
        <div className="banking-wallet-setup-panel">
          <BankOnboardingCard onApproved={onWalletApproved} />
        </div>
      )}

      <HelpdeskSupport.CardsGroup cards={getWalletHelpdeskArticles(t)} />
    </div>
  );
}

function BankingSidebar({
  items,
  currentScreen,
  provider,
  searchParams,
  chargebackOpenCount,
  navigate,
  t,
}) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  function buildPath(path) {
    const nextParams = new URLSearchParams(searchParams);
    // asaas is the URL default — only a stripe selection is worth carrying.
    if (provider === WALLET_PROVIDERS.STRIPE) {
      nextParams.set("provider", provider);
    } else {
      nextParams.delete("provider");
    }
    const query = nextParams.toString();
    return `${path}${query ? `?${query}` : ""}`;
  }

  return (
    <>
      <div
        className={`banking-sidebar-backdrop${open ? " is-visible" : ""}`}
        onClick={close}
      />
      <aside className={`banking-sidebar${open ? " is-open" : ""}`}>
        <button
          type="button"
          className="banking-sidebar__toggle"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? t("sidebar.closeMenu") : t("sidebar.openMenu")}
        >
          {open ? <IconX size={18} /> : <IconMenu2 size={18} />}
        </button>

        <div className="banking-sidebar__header">
          <Text.h5 contrast={9} weight={400}>
            Banking
          </Text.h5>
          <Text.caption contrast={6}>{t("sidebar.subtitle")}</Text.caption>
        </div>

        <nav className="banking-sidebar__menu">
          {items.map((item) => (
            <BankingSidebarButton
              key={item.key}
              item={item}
              active={currentScreen === item.key}
              disabled={item.disabled}
              countBadge={
                item.key === "chargebacks" && chargebackOpenCount > 0
                  ? chargebackOpenCount
                  : undefined
              }
              onClick={() => {
                if (item.disabled || !item.path) return;
                close();
                navigate(buildPath(item.path));
              }}
            />
          ))}
        </nav>
      </aside>
    </>
  );
}

function BankingSidebarButton({ item, active, disabled, countBadge, onClick }) {
  return (
    <button
      type="button"
      className={`banking-sidebar__item${active ? " active" : ""}${disabled ? " disabled" : ""}`}
      onClick={onClick}
      title={item.label}
      disabled={disabled}
    >
      <span className="banking-sidebar__item-icon">{item.icon}</span>
      <span className="banking-sidebar__item-copy">
        <Text.h6 contrast={active ? 8 : 7} weight={400}>
          {item.label}
        </Text.h6>
        <Text.span contrast={active ? 7 : 6}>{item.sidebarTitle}</Text.span>
      </span>
      {(item.badge || countBadge) && (
        <span className="banking-sidebar__badge">
          {countBadge || item.badge}
        </span>
      )}
    </button>
  );
}
